import Head from 'next/head'
import { useRouter } from 'next/navigation'
import { BRAND } from '@/constants'
import { getSiteName } from '@/helpers/environment'
import { IntlProvider, Translations, useIntl } from '@/intl'
import customOnError from '@/intl/customIntlOnError'
import { relativePastTimeResolver } from '@/intl/relativePastTimeResolver'
import translationsRepository from '@/intl/translations'
import { Locale } from '@/intl/types'
import { BrandName } from '@/scopes/common/domain/types'
import { Configuration } from '@/scopes/common/domain/types/configuration'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { ConfigurationContext, useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import configRepository from '@/scopes/common/infrastructure/config/configRepository'
import { CommonDependenciesProvider } from '@/scopes/common/infrastructure/dependencies/common'
import featureFlags from '@/scopes/common/infrastructure/feature-flags'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import { createPriceDetailsApiClient } from '@/scopes/common/infrastructure/price-details/api/priceDetailsApiClient'
import { createQueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import { userAuthenticationClient } from '@/scopes/common/infrastructure/user'

interface BrandStyleSettings {
  hasIllustration: boolean
  designSystemBrandName: string
  logoAlt: string
  headerTopBackgroundColor: string
  headerMainBackgroundColor: string
  cBtnBackgroundColor: string
  cBtnColor: string
  cBtnBorderColor: string
  cBtnBorderRadius: string
  cBtnTextTransform?: string
  cBoxBackgroundColor: string
  textColor: string
  linkColor: string
  linkTextDecoration?: string
  headingColor?: string
  headingTextTransform?: string
  logoWidthDefault: string
  logoWidthSm: string
  logoWidthSml: string
}

const assetsSettings = {
  designSystemUrls: {
    logo: '/DesignSystem/WebAssets/{DESIGN_SYSTEM_BRAND_NAME}/img/brand-logo.svg',
    illustration: '/DesignSystem/WebAssets/{DESIGN_SYSTEM_BRAND_NAME}/img/maintenance-illustration.svg',
    favicon: '/DesignSystem/WebAssets/{DESIGN_SYSTEM_BRAND_NAME}/img/favicon.ico',
  },
}

const defaultBrand = BRAND.SUNWEB
const brandStyleSettings: Record<string, BrandStyleSettings> = {
  [BRAND.SUNWEB]: {
    hasIllustration: true,
    designSystemBrandName: 'sunweb',
    logoAlt: 'Sunweb',
    headerTopBackgroundColor: '#fbf9f6',
    headerMainBackgroundColor: '#fff',
    cBtnBackgroundColor: '#fff',
    cBtnColor: '#362f2f',
    cBtnBorderColor: '#ff333f',
    cBtnBorderRadius: '1000px',
    cBoxBackgroundColor: '#fbf9f6',
    textColor: '#362f2f',
    linkColor: '#362f2f',
    linkTextDecoration: 'underline',
    logoWidthDefault: '120px',
    logoWidthSm: '140px',
    logoWidthSml: '140px',
  },
  [BRAND.ELIZA]: {
    hasIllustration: false,
    designSystemBrandName: 'eliza',
    logoAlt: 'Eliza was here',
    headerTopBackgroundColor: '#eee5d3',
    headerMainBackgroundColor: '#f9f6f0',
    cBtnBackgroundColor: '#ff0059',
    cBtnColor: '#fff',
    cBtnBorderColor: '#ff0059',
    cBtnBorderRadius: '5px',
    cBoxBackgroundColor: '#fff',
    textColor: '#2d0221',
    linkColor: '#0074fc',
    logoWidthDefault: '71px',
    logoWidthSm: '71px',
    logoWidthSml: '89px',
  },
}

interface Page500Props {
  brand: BrandName
  locale: Locale
  config?: Configuration
  messages: Translations
}

export function Page500({ brand, locale, config, messages }: Page500Props) {
  const nextRouter = useRouter()

  return (
    <div>
      <CommonDependenciesProvider
        dependencies={{
          env: {
            MEDIA_SERVER_BASE_URL: process.env.NEXT_PUBLIC_MEDIA_SERVER_BASE_URL,
            NEWRELIC_SCRIPT_NAME: process.env.NEXT_PUBLIC_NEWRELIC_SCRIPT_NAME,
          },
          relativePastTimeResolver,
          analyticsClient: ga4analyticsClient,
          featureFlagsStore: featureFlags.store,
          priceDetailsApiClient: createPriceDetailsApiClient({ contextItemId: 'random-context-item-id' }), // Not needed for 500 error page we just need to pass a random context item id
          queryStringClient: createQueryStringClient({ frameworkRouter: nextRouter }),
          userAuthenticationClient,
          logger: createNewRelicLogger({ scope: '500-page' }),
        }}
      >
        <ConfigurationContext value={config ?? null}>
          <IntlProvider locale={locale} messages={messages} onError={customOnError}>
            <Custom500 brand={brand} />
          </IntlProvider>
        </ConfigurationContext>
      </CommonDependenciesProvider>
    </div>
  )
}

interface Error500Props {
  brand: string
}

export function Custom500({ brand }: Error500Props) {
  const intl = useIntl()
  const styleSettings = brandStyleSettings[brand] || brandStyleSettings[defaultBrand]

  const dsBrandName = styleSettings.designSystemBrandName
  const logoUrl = assetsSettings.designSystemUrls.logo.replace('{DESIGN_SYSTEM_BRAND_NAME}', dsBrandName)
  const illustrationUrl = assetsSettings.designSystemUrls.illustration.replace(
    '{DESIGN_SYSTEM_BRAND_NAME}',
    dsBrandName,
  )
  const logoAlt = styleSettings.logoAlt
  const supportLink = useConfiguration('links.support')

  return (
    <>
      <Head>
        <title>{intl.formatMessage({ id: '500_title' })}</title>
      </Head>
      {getStyle(styleSettings)}
      {getHeader(logoUrl, logoAlt)}
      <main
        id="main-content"
        tabIndex={-1}
        className="o-block__body o-wrapper l-site__wrapper--large l-site-main"
        style={{ fontFamily: 'General Sans,Arial,Sans-serif' }}
      >
        <div className="o-wrapper l-site__wrapper--medium l-site-main__content">
          <div className="o-wrapper l-site__wrapper--small">
            <div className="t-error__container">
              {styleSettings.hasIllustration && (
                <div className="t-error__item t-error__item--illustration">
                  <img className="t-error__illustration" src={illustrationUrl} alt="" />
                </div>
              )}
              <div className="t-error__item t-error__item--content">
                <h1 className="c-title m-heading m-heading--medium t-error__title">
                  {intl.formatMessage({ id: '500_heading' })}
                </h1>
                <p className="t-error__intro-text">{intl.formatMessage({ id: '500_paragraph' })}</p>
                <div className="c-box o-box t-error__box cs-default--light">
                  <div className="c-box__content o-box__content">
                    <ul className="sp-stack-children--small t-error__options">
                      <li>
                        <span>
                          {intl.formatMessage(
                            { id: '500_solution1' },
                            {
                              s1: (chunks: (string | React.JSX.Element)[]) => <strong>{chunks}</strong>,
                            },
                          )}
                        </span>
                      </li>
                      <li>
                        <span>
                          {intl.formatMessage(
                            { id: '500_solution2' },
                            {
                              s1: (chunks: (string | React.JSX.Element)[]) => <strong>{chunks}</strong>,
                              a1: (chunks: (string | React.JSX.Element)[]) => <a href={supportLink}>{chunks}</a>,
                            },
                          )}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
                <a className="c-btn c-btn--default" href="/" title={intl.formatMessage({ id: '500_buttonText' })}>
                  <span className="c-btn__text">{intl.formatMessage({ id: '500_buttonText' })}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

function getHeader(logoUrl: string, logoAlt: string) {
  return (
    <header className="o-block__header l-site-header cs-default">
      <div className="l-site-header__top cs-default cs-default--light">
        <div className="o-wrapper l-site__wrapper--medium">
          <div className="o-pack o-pack--auto o-pack--middle">
            <div className="o-pack__item l-site-header__top-left" />
            <div className="o-pack__item o-pack__item--wide l-site-header__top-right" />
          </div>
        </div>
      </div>
      <div className="l-site-header__main">
        <div className="o-wrapper l-site__wrapper--medium">
          <div className="o-pack o-pack--auto o-pack--middle">
            <div className="o-pack__item l-site-header__main-left">
              <a className="l-site-header__logo" href="/">
                <img className="l-site-header__logo-img" src={logoUrl} alt={logoAlt} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

function getStyle(styleSettings: BrandStyleSettings) {
  return (
    <style
      // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
      dangerouslySetInnerHTML={{
        __html: `
          /* Reset */
          ul {
            margin: 0;
          }

          /* Basic */
          html {
            color: ${styleSettings.textColor};
          }

          a {
            color: ${styleSettings.linkColor};
            text-decoration: ${styleSettings.linkTextDecoration || 'none'};
          }

          /* Error template */
          .t-error__container > * {
            margin-bottom: 24px;
          }

          .t-error__container > :empty,
          .t-error__container > :last-child {
            margin-bottom: 0;
          }

          .t-error__item--illustration {
            padding-left: 48px;
            padding-right: 48px;
          }

          .t-error__item--illustration .t-error__illustration {
            display: block;
            margin: 0 auto;
            max-width: 350px;
            width: 100%;
          }

          .t-error__item--content {
            text-align: center;
          }

          .t-error__item--content > * {
            margin-bottom: 24px;
          }

          .t-error__item--content > :empty,
          .t-error__item--content > :last-child {
            margin-bottom: 0;
          }

          .t-error__item--content .t-error__title {
            margin-bottom: 12px;
          }

          .t-error__item--content .t-error__box {
            padding: 24px;
          }

          .t-error__item--content .t-error__options {
            font-size: 15px;
            text-align: left;
          }

          @media (min-width: 840px) {
            .t-error__container {
              align-content: center;
              display: flex;
              flex-direction: row-reverse;
              flex-wrap: nowrap;
              justify-content: center;
              margin-top: 24px;
            }

            .t-error__item {
              flex-basis: 50%;
            }

            .t-error__item--illustration {
              padding-left: 24px;
              padding-right: 0;
            }

            .t-error__item--illustration .t-error__illustration {
              max-width: 350px;
            }
          }

          /* Components */
          .c-box {
            background-color: #fff;
            border: 2px solid #d9d6d1;
            border-radius: 8px;
            box-shadow: 1px 1px 5px 0 rgba(54, 47, 47, 0.1);
            padding: 16px;
          }

          .cs-default--light.c-box {
            background-color: ${styleSettings.cBoxBackgroundColor};
            border-color: #edebe7;
          }

          .c-btn,
          a.c-btn {
            background-color: ${styleSettings.cBtnBackgroundColor};
            border: 2px solid ${styleSettings.cBtnBorderColor};
            border-radius: ${styleSettings.cBtnBorderRadius};
            color: ${styleSettings.cBtnColor};
            cursor: pointer;
            display: inline-block;
            font-family:
              General Sans,
              Arial,
              Sans-serif;
            font-size: 16px;
            font-weight: 600;
            line-height: 1.5;
            min-width: 120px;
            padding: 10px 22px;
            text-align: center;
            text-decoration: none;
            text-transform: unset;
            transition-duration: 0.3s;
            transition-property: color, background-color, border-color, box-shadow;
            text-transform: ${styleSettings.cBtnTextTransform || 'none'};
          }

          .sp-stack-children--small > * {
            margin-bottom: 12px;
          }

          .sp-stack-children--small > :empty,
          .sp-stack-children--small > :last-child {
            margin-bottom: 0;
          }

          /* Body */
          .l-site {
            min-height: 100vh;
            padding: 0;
            margin: 0;
          }

          .o-block--stretch {
            display: flex;
            flex-direction: column;
            width: 100%;
          }

          .o-block {
            text-align: left;
          }

          /* header */
          .l-site-header {
            box-shadow: 1px 1px 5px 0 rgba(54, 47, 47, 0.1);
          }
          .o-block__header {
            display: block;
          }
          .cs-default {
            background-color: ${styleSettings.headerMainBackgroundColor};
            border-color: #edebe7;
            color: #362f2f;
          }

          .l-site-header__top {
            padding-bottom: 8px;
            padding-top: 8px;
          }

          .cs-default--light {
            background-color: ${styleSettings.headerTopBackgroundColor};
            color: ${styleSettings.textColor};
          }

          /* Texts */
          .m-heading {
            font-size: 24px;
            font-weight: 700;
            color: ${styleSettings.headingColor || styleSettings.textColor};
            text-transform: ${styleSettings.headingTextTransform || 'none'};
          }

          /* Main header */
          @media (min-width: 840px) {
            .l-site-header__main {
              padding-bottom: 8px;
              padding-top: 12px;
            }
          }

          .l-site-header__main {
            padding-bottom: 8px;
            padding-top: 8px;
          }

          .o-pack--auto {
            table-layout: auto;
          }
          .o-pack {
            border-collapse: separate;
            border-spacing: 0;
            display: table;
            margin-left: 0;
            table-layout: fixed;
            width: 100%;
          }

          .o-pack--middle > .o-pack__item {
            vertical-align: middle;
          }

          @media (min-width: 840px) {
            .l-site-header__main-left {
              padding-bottom: 8px;
              vertical-align: bottom;
            }
          }

          .l-site-header__main-left {
            padding-right: 48px;
          }

          .o-pack__item {
            display: table-cell;
            vertical-align: top;
          }

          .cs-default a {
            color: #362f2f;
          }
          .l-site-header__logo {
            display: inline-block;
            font-weight: 500;
            text-decoration: underline;
          }

          .l-site-header__logo-img {
            height: auto;
            width: ${styleSettings.logoWidthDefault};
          }

          @media (min-width: 600px) {
            .l-site-header__logo-img {
              width: ${styleSettings.logoWidthSm};
            }
          }

          @media (min-width: 840px) {
            .l-site-header__logo-img {
              width: ${styleSettings.logoWidthSml};
            }
          }

          /* Site Wrapper */
          .o-wrapper {
            margin-left: auto;
            margin-right: auto;
            width: 100%;
            box-sizing: border-box;
          }

          .o-wrapper:after {
            clear: both;
            content: '';
            display: block;
          }

          @media (min-width: 840px) {
            .l-site__wrapper--medium {
              padding-left: 24px;
              padding-right: 24px;
            }
          }

          @media (min-width: 1280px) {
            .l-site__wrapper--medium {
              max-width: 80rem;
            }
          }

          @media (min-width: 1440px) {
            .l-site__wrapper--medium {
              max-width: 90rem;
            }
          }

          .l-site__wrapper--medium {
            padding-left: 12px;
            padding-right: 12px;
          }

          .o-block--stretch > .o-block__body {
            flex-basis: auto;
            flex-grow: 1;
            flex-shrink: 0;
          }

          @media (min-width: 600px) {
            .l-site-main {
              padding-bottom: 48px;
              padding-top: 20px;
            }
          }

          @media (min-width: 840px) {
            .l-site-main {
              padding-bottom: 48px;
              padding-top: 4px;
            }
          }

          @media (min-width: 1600px) {
            .l-site__wrapper--large {
              max-width: 100rem;
            }
          }

          .l-site-main {
            padding-bottom: 24px;
            padding-top: 4px;
          }

          .o-block__body {
            display: block;
          }

          @media (min-width: 600px) {
            .l-site__wrapper--small {
              max-width: 37.5rem;
            }
          }

          @media (min-width: 840px) {
            .l-site__wrapper--small {
              max-width: 52.5rem;
            }
          }
          `,
      }}
    />
  )
}

export async function getStaticProps() {
  const siteName = getSiteName()
  const configuration = await configRepository.get({ siteName })
  const locale = configuration.locale
  const brand = configuration.brandName
  const messages = await translationsRepository.getFor({ locale, brand })
  const styleSettings = brandStyleSettings[brand] || brandStyleSettings[defaultBrand]
  const faviconUrl = assetsSettings.designSystemUrls.favicon.replace(
    '{DESIGN_SYSTEM_BRAND_NAME}',
    styleSettings.designSystemBrandName,
  )

  return {
    props: {
      messages,
      locale,
      config: configuration,
      metadata: {
        faviconUrl,
      },
      brand,
      page: {
        fields: {
          htmlLang: locale,
          bodyAttrs: {
            staticFileName: 'error',
          },
        },
      },
      reactComponentsList: [],
    },
  }
}

export default Page500
